import { Button } from "@/components/ui/button"
import { useCallback, useContext, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { X } from "lucide-react"
import AnalyzeOrderVerify from "@/components/maps/AnalyzeOrderVerify"
import {
  OrderMetaData,
  StopMetaData,
} from "@/components/maps/AnalyzeOrderVerifyStops"
import { RouteStop } from "@/reducers/routes2gSlice"
import { OrderAnalyzeRoute } from "@/lib/orderAnalyzeRouteParser"
import AnalyzeOrderOnPageMap from "./AnalyzeOrderOnPageMap"
import { RouteInfo } from "@/components/maps/RouteView"
import { v4 as uuidv4 } from "uuid"
import AdHocCommands, {
  AvoidType,
  LoadType,
  NaviSelectionInput,
} from "@/api/adHocCommands"
import PlanOrderOnPage from "@/components/analyzeOrderOnPage/PlanOrderOnPage"
import { generateRouteName, saveRoute } from "@/lib/saveRoutes"
import { XmppContext } from "@/stream/xmppClient"
import PlanRouteParser, { AlternativeRoute } from "@/lib/planRouteParser"
import {
  carConfigs,
  VEHICLE_TYPE_NAMES,
  VehicleType,
} from "@/components/analyzeOrderOnPage/VehicleTypeSelect"
import { SelectedDriverLocation } from "@/components/analyzeOrderOnPage/DriverSelect"
import { useDetailedReverseGeocoder } from "@/hooks/useReverseGeocode"

interface AnalyzeOrderModalProps {
  isOpen: boolean
  onClose: () => void
  extractedData: any
  fileName: string | null
  fileUrl: string | null
  isProcessing: boolean
  initialStep?: string
  sharedRouteState?: any
  onLoadSavedRoute?: (routeId: string) => void
  onReset: () => void
  initialAvoidOptions?: AvoidType[]
  initialLoadType?: LoadType[]
  initialVehicleType?: VehicleType
  initialDriver?: SelectedDriverLocation | undefined
}

export default function AnalyzeOrderModal({
  isOpen,
  onClose,
  extractedData,
  fileName,
  fileUrl,
  isProcessing,
  initialStep = "verify",
  sharedRouteState,
  onLoadSavedRoute,
  onReset,
  initialAvoidOptions,
  initialLoadType,
  initialVehicleType,
  initialDriver,
}: AnalyzeOrderModalProps) {
  const { t } = useTranslation()
  const { client, myJid } = useContext(XmppContext)
  const reverseGeocoder = useDetailedReverseGeocoder()

  const [routeData, setRouteData] = useState<OrderAnalyzeRoute | undefined>(
    undefined,
  )
  const [isFetchingPolylines, setIsFetchingPolylines] = useState(false)
  const [isRouteFetched, setIsRouteFetched] = useState(false)

  const [step, setStep] = useState<string>(initialStep)

  // Marcos/Nawigator order created at the verify step ("Generate route"), shown
  // as a confirmation banner on the map step. Selection is kept only so the
  // verify step can report it; the on-page flow has no finalize/send step.
  const [naviTransportId, setNaviTransportId] = useState<number | undefined>(
    undefined,
  )
  const [, setNaviSelection] = useState<NaviSelectionInput | undefined>(
    undefined,
  )

  const handleClose = useCallback(
    (force?: boolean) => {
      if (force) {
        onClose()
        return
      } else if (
        window.XMPP &&
        isRouteFetched &&
        routeData?.stops &&
        routeData?.stops.length >= 2
      ) {
        saveRoute(
          { stops: routeData.stops, jid: routeData.jid },
          routeData.route,
          routeData.avoidOptions || [],
          routeData.loadType || [],
          routeData.vehicleType ?? "tir",
          routeData.routeMetaData?.messageText,
          {
            fileName: fileName ?? undefined,
            fileUrl: fileUrl ?? undefined,
            extractedData: routeData?.routeMetaData,
            driverCarConfig: routeData.driverCarConfig,
            customName: `[${routeData.driverCarConfig ? "Driver" : VEHICLE_TYPE_NAMES[routeData.vehicleType ?? "tir"]}] - ${generateRouteName(routeData.stops)}`,
            alternativeRoutes: routeData.alternativeRoutes,
            perLegAlternatives: routeData.perLegAlternatives,
          },
        )

        onClose()
      }
      onClose()
    },
    [fileName, fileUrl, isRouteFetched, onClose, routeData],
  )

  useEffect(() => {
    if (!sharedRouteState) {
      setRouteData({
        jid: "on-page",
        stops: [],
        route: {
          routes: [],
          waypoints: [],
          sections: [],
        },
        routeMetaData: extractedData,
      })
    }
  }, [extractedData, sharedRouteState])

  useEffect(() => {
    if (sharedRouteState) {
      setRouteData({
        jid: "on-page",
        stops: sharedRouteState.temporaryRoute?.stops,
        lastUpdatedBy: sharedRouteState.temporaryRoute?.lastUpdatedBy,
        route: sharedRouteState.temporaryRouteInfo,
        routeMetaData: sharedRouteState.extractedData,
      })
      setIsRouteFetched(true)
    }
  }, [sharedRouteState])

  const handleOnEdited = useCallback(() => {
    window.analytics?.track(
      window.anonymousXMPP
        ? "Anonymous_EditedMetaData"
        : "OrderOnPage_EditedMetaData",
    )
    setIsRouteFetched(false)
  }, [setIsRouteFetched])

  const handleBackToVerify = useCallback((routeData: OrderAnalyzeRoute) => {
    window.analytics?.track(
      window.anonymousXMPP
        ? "Anonymous_BackToVerify"
        : "OrderOnPage_BackToVerify",
    )
    setRouteData(routeData)
    setStep("verify")
  }, [])

  const fetchPolylines = useCallback(
    async (
      stops: RouteStop[],
      avoidOptions: AvoidType[],
      loadType: LoadType[],
      carConfig?: any,
    ): Promise<
      | [RouteInfo | undefined, RouteStop[], AlternativeRoute[] | undefined]
      | undefined
    > => {
      if (!client || !myJid) {
        console.warn("XMPP client not available")
        return [undefined, stops, undefined]
      }

      // Prepare stops with addressUpdated flag for XMPP processing
      const stopsForXmpp = stops.map((stop) => ({
        ...stop,
        id: stop.id ?? uuidv4(),
        addressUpdated: true,
        route_handle: undefined,
        is_temporary: true,
      }))

      if (stopsForXmpp.length > 0) {
        stopsForXmpp[0].remaining_time = 0
        stopsForXmpp[0].remaining_distance = 0
      }

      if (stopsForXmpp.length < 2) {
        const firstStop = stopsForXmpp[0]
        const [lat, lon] =
          firstStop?.location.formattedAddress?.split(",") ?? ","

        console.log("[DEBUG] First stop ", lat, lon)
        const location = await reverseGeocoder(lat, lon)

        stopsForXmpp[0] = {
          ...stopsForXmpp[0],
          action: stopsForXmpp[0].action ?? "waypoint",
          id: stopsForXmpp[0].id ?? uuidv4(),
          is_active: true,
          is_temporary: true,
          location: {
            ...location,
          },
        }

        return [undefined, stopsForXmpp, undefined]
      }

      try {
        console.debug(
          "Fetching order polylines via XMPP with avoid:",
          avoidOptions,
          "and loadType:",
          loadType,
          "car config:",
          carConfig,
        )

        const result = await AdHocCommands.getRoutePlan(
          client,
          myJid.toString(),
          stopsForXmpp,
          {
            ...carConfig,
            avoid: avoidOptions,
            loadType: loadType,
          },
        )

        const parsed = PlanRouteParser.parse(result)

        if (parsed?.route && parsed?.stops) {
          return [
            parsed.route,
            [stopsForXmpp[0], ...parsed.stops] as RouteStop[],
            parsed.alternativeRoutes,
          ]
        }

        console.warn("Failed to parse route data from XMPP response")
        return [undefined, stopsForXmpp, undefined]
      } catch (err) {
        console.warn("Route calculation error:", err)
        window.mapToaster?.show({
          message: t("planOrder.routeToasts.calculateFailed"),
          intent: "danger",
          timeout: 3000,
        })
        return [undefined, stopsForXmpp, undefined]
      }
    },
    [client, myJid, reverseGeocoder, t],
  )

  const handleContinue = useCallback(
    async (metaData: OrderMetaData, shouldFetch?: boolean) => {
      window.analytics?.track(
        window.anonymousXMPP
          ? "Anonymous_ContinueToMap"
          : "OrderOnPage_ContinueToMap",
        {
          metaData,
        },
      )
      setIsFetchingPolylines(true)
      const newStops = metaData.stops!.map((metaDataStop: StopMetaData) => {
        const scheduledTimeMax = metaDataStop.scheduled_hour_max
          ? metaDataStop.scheduled_hour_max
          : "23:59:00"
        const scheduledDate = metaDataStop.scheduled_arrival_time
          ? metaDataStop.scheduled_arrival_time.split("T")[0]
          : undefined
        const scheduled_arrival_time = scheduledDate
          ? `${scheduledDate}T${scheduledTimeMax}`
          : undefined
        return {
          action: metaDataStop.action,
          id: uuidv4(),
          location: {
            formattedAddress:
              metaDataStop.place?.formattedAddress ??
              ` ${metaDataStop.location?.formattedAddress}`,
            companyName: metaDataStop.location.companyName,
            postalCode: metaDataStop.place?.addressComponents
              ? (metaDataStop.place.addressComponents.find((c) =>
                  c.types.includes("postal_code"),
                )?.longText ?? metaDataStop.location?.postalCode)
              : metaDataStop.location?.postalCode,
            // place.location holds LatLng methods (lat()/lng()), and Location
            // expects `lon`, not `lng` — the old code silently produced no
            // coordinates. Prefer the picked place; fall back to the
            // extraction's coordinates (e.g. GPS printed in the document).
            lat: metaDataStop.place?.location
              ? String(metaDataStop.place.location.lat())
              : metaDataStop.location?.lat,
            lon: metaDataStop.place?.location
              ? String(metaDataStop.place.location.lng())
              : metaDataStop.location?.lon,
          },
          proximity_status: "far",
          addressUpdated: false,
          action_reference_number: metaDataStop.action_reference_number,
          ref: metaData.order_number,
          scheduled_hour_min: metaDataStop.scheduled_hour_min,
          scheduled_hour_max: metaDataStop.scheduled_hour_max,
          pallet_count: metaDataStop.pallet_count,
          weight_kg: metaDataStop.weight_kg,
          scheduled_arrival_time,
        } as RouteStop
      })

      try {
        if (!isRouteFetched || shouldFetch) {
          const [routeData, geocodedStops, alternatives] =
            (await fetchPolylines(
              newStops,
              initialAvoidOptions || [],
              initialLoadType || [],
              initialVehicleType
                ? carConfigs[initialVehicleType]
                : carConfigs["tir"],
            )) || []
          if (routeData && geocodedStops) {
            const modifiedRouteData: OrderAnalyzeRoute = {
              jid: "on-page",
              lastUpdatedBy: "test",
              route: {
                routes: routeData.routes,
                tolls: routeData.routes?.[0]?.tolls || [],
                waypoints: [
                  ...geocodedStops.map((stop: RouteStop) => ({
                    location: [
                      Number(stop.location.lat),
                      Number(stop.location.lon),
                    ] as [number, number],
                  })),
                ],
                sections: [],
              },
              stops: geocodedStops,
              routeMetaData: metaData,
              alternativeRoutes: alternatives,
            }
            setRouteData(modifiedRouteData)
            setIsRouteFetched(true)
          }
        }
        setStep("map")
      } catch (error) {
        console.error(error)
      } finally {
        setIsFetchingPolylines(false)
      }
    },
    [
      fetchPolylines,
      initialAvoidOptions,
      initialLoadType,
      initialVehicleType,
      isRouteFetched,
    ],
  )

  if (!isOpen) return null

  // @ts-ignore
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center rounded-md bg-black/70 bg-opacity-50">
      <div className="relative h-full w-full bg-white">
        <Button
          variant="ghost"
          onClick={() => handleClose()}
          className="absolute right-4 top-4 z-10 h-10 w-10 rounded-full bg-white/90 p-0 shadow-lg hover:bg-white"
        >
          <X className="h-5 w-5" />
        </Button>
        <div className="h-full w-full">
          {step === "verify" && (
            <AnalyzeOrderVerify
              onCloseFullScreen={() => handleClose(false)}
              stops={routeData?.routeMetaData?.stops}
              routeMetaData={routeData?.routeMetaData}
              files={[
                {
                  name: fileName || sharedRouteState?.fileName,
                  url: fileUrl || sharedRouteState?.fileUrl,
                },
              ]}
              onContinue={handleContinue}
              isFetchingPolylines={isFetchingPolylines}
              isFetchingPolylinesFailed={false}
              polylineErrors={null}
              onClearPolylineErrors={() => {}}
              isRouteFetched={isRouteFetched}
              onEdited={handleOnEdited}
              onResetMessageGenerated={() => {}}
              showSkipStepButton={false}
              isLoading={isProcessing}
              planOrderNavi={{
                driverJid: initialDriver?.driverJid,
                setNaviTransportId,
                setNaviSelection,
              }}
            />
          )}
          {step === "map" && (
            <AnalyzeOrderOnPageMap
              onFetchPolylines={fetchPolylines}
              routeData={routeData}
              onBackToVerify={handleBackToVerify}
              sharedRouteState={sharedRouteState}
              fileUrl={fileUrl || sharedRouteState?.fileUrl}
              fileName={fileName || sharedRouteState?.fileName}
              extractedData={extractedData || sharedRouteState?.extractedData}
              initialVehicleType={initialVehicleType}
              initialAvoidOptions={initialAvoidOptions}
              initialDriver={initialDriver}
              initialLoadType={initialLoadType}
              onClose={handleClose}
              onLoadSavedRoute={onLoadSavedRoute}
              onReset={onReset}
              naviTransportId={naviTransportId}
            />
          )}
          {step === "only-map" && (
            <PlanOrderOnPage
              onFetchPolylines={fetchPolylines}
              onClose={handleClose}
            />
          )}
        </div>
      </div>
    </div>
  )
}
